import React, { useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import selectAnalytics from './selectAnalytics';
import Price from '../Product/Price';
import { toggleFavourite, searchInput } from '../../actions/environment';
import { addClass, removeClass } from '../../utils/dom';
import { productImageSizeTranslation } from '../../utils/image';
import { translate, prefix, translateObjectData } from '../../utils/locale';
import { urlify, rootUrl, baseName } from '../../utils/string';
import s from './ProductItem.scss';

let productContainerEl = null;

const ProductItem = ({ variantClasses, content, setup, currentPath, currentHash, currentView, canonical, searchInputText, imageSize, itemIndex = 1, favourites, ...props }) => {
	const productContainer = useRef(null);
	content = translateObjectData(setup, content);
	let productItemClasses = [s.productItem];
	if (content.variant_text_color_bool) productItemClasses = [s.productItem, s.productItem__blackText];
	if (variantClasses) productItemClasses = [...productItemClasses, ...(Array.isArray(variantClasses) ? variantClasses.filter((c) => !!c && c.indexOf('productItem') !== -1).map((c) => s[c]) : [s[variantClasses.toString()]])];
	const path = canonical ? translate(setup, content, 'canonicalUri') : `${currentPath}/${content.uri}${currentHash}`;
	const isNew = Object.keys(content.categories).filter((c) => content.categories[c].uri.indexOf('new') !== -1).length > 0;
	const categoryGender = currentPath.indexOf('woman') !== -1 ? 'woman' : 'man';
	const style = {};

	if (content.productHidden) return null;

	if (!content.inStock) productItemClasses.push(s.productItem__soldOut);
	if (content.price.showAsOnSale) productItemClasses.push(s.productItem__sale);
	if (isNew && currentPath.indexOf('new') === -1) productItemClasses.push(s.productItem__new);

	const badges = [];
	let newSet = false;
	let saleSet = false;

	if (content.pr_badges) {
		Object.keys(content.pr_badges).forEach((badge) => {
			const badgeItem = content.pr_badges[badge];
			if (!!badgeItem.isNew && !content.price.newProduct) return;
			if (!!badgeItem.isNew && content.price.newProduct) newSet = true;
			if (!!badgeItem.isSale && !content.price.showAsOnSale) return;
			if (!!badgeItem.isSale && content.price.showAsOnSale) saleSet = true;
			if (!!badgeItem.isSoldOut && content.inStock) return;
			if (badgeItem.image) {
				badges.push((
					<span key={badge} className={[s.productItem_link_info_badge, s.productItem_link_info_badge__svg].join(' ')}>
						<img src={badgeItem.image.url} alt="" />
					</span>
				));
			}
		});
	}

	if (content.variant_coming_soon_bool && currentPath.indexOf('coming-soon') === -1) {
		badges.push(<span key="coming-soon-badge" className={[s.productItem_link_info_badge, s.productItem_link_info_badge__horizontal].join(' ')}>{ translate(setup, 'comingSoon').split('').join(setup.breakPoint === 'mobile' ? '\n' : ' ') }</span>);
	} else if (content.inStock && isNew && currentPath.indexOf('new') === -1 && !newSet) {
		badges.push(<span key="new-badge" className={s.productItem_link_info_badge}>{ translate(setup, 'new').split('').join('\n') }</span>);
	} else if (content?.offer_text?.text && content?.offer_text?.position !== 'title') { // eslint-disable-line camelcase
		const extraProps = {};
		if (content?.offer_text?.color) extraProps.style = { color: content.offer_text.color }; // eslint-disable-line camelcase
		badges.push(<span key={content.offer_text.text} className={[s.productItem_link_info_badge, (content?.offer_text?.position ? s[`productItem_link_info_badge__${content.offer_text.position}`] : null)].join(setup.breakPoint === 'mobile' ? '\n' : ' ')} {...extraProps}>{ content.offer_text.text.split('').join('\n') }</span>); // eslint-disable-line camelcase
	}

	const clearSearch = () => {
		if (searchInputText !== '') props.searchInput('');
		props.closeSearch();
		// Clear queue in listing to get all batched impressions.
		selectAnalytics([content], setup, currentView, itemIndex);
	};

	const hoverHandler = (e) => {
		if (e.type === 'touchstart' && productContainer?.current) {
			if (productContainerEl) removeClass(productContainerEl, 'u_hovering');
			productContainerEl = productContainer?.current;
			addClass(productContainer.current, 'u_hovering');
		}
	};

	const toggleFavouriteHandler = (e) => {
		const value = e.currentTarget.value;
		props.toggleFavourite(value, currentPath);
	};

	return (
		<div className={productItemClasses.join(' ')} ref={productContainer}>
			<button className={s.productItem_favourite} value={content.groupId} type="button" aria-label="Add to favourites" onClick={toggleFavouriteHandler}>
				<svg width="14" height="22" viewBox="0 0 14 22" xmlns="http://www.w3.org/2000/svg">
					<path d={`M0 0V22L7 15.1075L14 22V0H0Z${favourites.indexOf(content.groupId) !== -1 ? '' : 'M12.8649 19.3016L7 13.5269L1.13513 19.3016V1.11769H12.8648L12.8649 19.3016Z'}`} />
				</svg>
			</button>
			<Link className={s.productItem_link} to={prefix(setup, path)} onClick={clearSearch} onTouchStart={hoverHandler} onTouchEnd={hoverHandler}>
				<div className={[s.productItem_link_images, 'u_productImages'].join(' ')}>
					{
						content?.media?.[categoryGender] && content.media[categoryGender].slice(0, 2).map((image) => (image && <img key={image.slice(image.lastIndexOf('/') + 1)} {...productImageSizeTranslation(imageSize, image)} alt={`${content.brand} ${content.name}`} />))
					}
					{
						!content?.media?.[categoryGender] && content?.image && content.image.map((image) => (image && <img key={image.slice(image.lastIndexOf('/') + 1)} {...productImageSizeTranslation(imageSize, image)} alt={`${content.brand} ${content.name}`} />))
					}
				</div>
				<div className={s.productItem_link_info}>
					<div className={s.productItem_link_info_sizes}>
						{
							content.sizes.reduce((acc, size) => {
								if (size.name.indexOf('/') !== -1) {
									const [w] = size.name.split('/');

									for (let i = 0; i < acc.length; i += 1) {
										if (acc[i].name.indexOf(w) !== -1) {
											if (acc[i].stock === 0 && size.stock > 0) acc[i].stock = 1;
											return acc;
										}
									}

									const newSize = { ...size };
									newSize.name = `${translate(setup, 'firstSizeGroupShort')} ${w}`;
									return [...acc, newSize];
								}
								return [...acc, size];
							}, []).map((size) => <span key={size.item} className={[s.productItem_link_info_size, (size.stock === 0 ? s.productItem_link_info_size__soldOut : null)].join(' ')}>{ size.name }</span>)
						}
					</div>
					<div className={s.productItem_link_info_badges}>
						{ badges }
					</div>
					<div className={s.productItem_link_info_details} style={style}>
						<div className={s.productItem_link_info_details_name}>
							{ content.name.split('(').map((titlePart, k) => <span key={urlify(titlePart)}>{`${k > 0 ? '(' : ''}${titlePart}`}</span>) }
							{/* eslint-disable-next-line camelcase */}
							{ content?.offer_text?.position === 'title' && <span style={{ ...(content.offer_text.color ? { color: content.offer_text.color } : {}) }}> { content.offer_text.text }</span> }
						</div>
						<span className={s.productItem_link_info_details_variant}>{ content.variantName }</span>
						<div className={s.productItem_link_info_details_price}>
							<Price price={content.price} comingSoon={content.variant_coming_soon_bool} soldOut={!content.inStock ? translate(setup, 'soldOut') : false} />
						</div>
					</div>
				</div>
			</Link>
		</div>
	);
};

const mapState = ({ environment }, { content, id, currentPath, variation }) => ({
	setup: environment.setup,
	searchInputText: environment.searchInputText,
	favourites: environment.favourites,
	content,
	id,
	currentPath,
	variation,
});

export default hot(module)(connect(mapState, { toggleFavourite, searchInput })(ProductItem));
